import { db } from "./firebase";
import { doc, writeBatch, increment, collection, serverTimestamp } from "firebase/firestore";
import AuditService, { AUDIT_MODULES, AUDIT_ACTIONS } from "./audit_service";
import XpService, { XP_ACTION_TYPES } from "./xp_service";
import GamificationEngineService from "./gamification_engine";
import { COLLECTIONS } from "../services/collections";
import { TRIAL_ATTENDANCE_STATUS } from "../constants/enrollments";

/**
 * AttendanceService
 * 
 * Responsável por salvar o controle de presença de uma sessão.
 * Opera de forma desnormalizada, atualizando em lote (Batch):
 *   1. Session.attendances  → Array consolidado de presenças na sessão
 *   2. Member.attendanceStats → Contadores individuais do aluno
 *   3. Member/attendanceLogs  → Subcoleção com histórico dia a dia
 *   4. Analytics global       → Contadores mensais da academia
 *
 * STATUS no Firestore:
 *   'ATTENDED'  → Presente
 *   'NO_SHOW'   → Ausente (faltou)
 *   'BOOKED'    → Agendado (chamada não realizada)
 */
const AttendanceService = {
    /**
     * @param {string} idTenant
     * @param {object} session - Sessão do Firestore ({ id, sessionDate, idClass, activityName, employeeName, startTime, ... })
     * @param {Array} membersState - Estado dos alunos vindo da UI ({ id, name, photo, status, oldStatus, justification, enrollmentType, idEnrollment })
     * @param {object} currentUser - Usuário logado ({ uid })
     * @param {object} [gamificationSettings] - Configurações de gamificação do tenant (opcional)
     */
    saveSessionAttendance: async (idTenant, session, membersState, currentUser, gamificationSettings = null) => {
        if (!idTenant) throw new Error("idTenant é obrigatório.");
        if (!session?.id) throw new Error("session.id é obrigatório.");
        if (!session.sessionDate) throw new Error("session.sessionDate é obrigatório.");
        if (!currentUser?.uid) throw new Error("currentUser.uid é obrigatório.");

        const batch = writeBatch(db);
        const sessionRef = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, session.id);
        const { id: idSession, sessionDate } = session;

        // -- Mapeamento Frontend → Backend --
        const toBackendStatus = (frontendStatus) => {
            if (frontendStatus === "present") return "ATTENDED";
            if (frontendStatus === "absent") return "NO_SHOW";
            return "BOOKED";
        };

        // ─── 1. Array de attendances na própria Session ───
        const finalAttendances = membersState.map(member => ({
            idMember: member.id,
            memberName: member.name,
            photo: member.photo || null,
            status: toBackendStatus(member.status),
            justification: member.justification || "",
            type: member.enrollmentType,
            idEnrollment: member.idEnrollment || null,
        }));

        batch.update(sessionRef, {
            attendances: finalAttendances,
            attendanceUpdatedBy: currentUser.uid,
            attendanceUpdatedAt: serverTimestamp(),
            attendanceCompleted: true,
        });

        // ─── 2 e 3. Atualizar cada Member + Gerar Log individual ───
        let totalNewPresents = 0;
        let totalNewAbsents = 0;
        const membersNewlyPresent = [];

        for (const member of membersState) {
            if (!member.id) continue;

            const isPresent = member.status === "present";
            const isAbsent = member.status === "absent";
            const wasPresent = member.oldStatus === "ATTENDED";
            const wasAbsent = member.oldStatus === "NO_SHOW";

            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, member.id);
            const updates = {};

            if (isPresent && !wasPresent) {
                updates["attendanceStats.presentCount"] = increment(1);
                updates["attendanceStats.lastAttended"] = sessionDate;
                updates["attendanceStats.consecutiveAbsences"] = 0;

                if (wasAbsent) {
                    updates["attendanceStats.absentCount"] = increment(-1);
                    totalNewAbsents -= 1;
                }
                totalNewPresents += 1;
                membersNewlyPresent.push(member.id);

                // ── XP por presença ──
                const xpPerPresence = gamificationSettings?.memberAttendancePresent || 0;
                if (xpPerPresence > 0) {
                    XpService.addXpInBatch(batch, idTenant, {
                        idMember: member.id,
                        xpAmount: xpPerPresence,
                        actionType: XP_ACTION_TYPES.CLASS_ATTENDANCE,
                        sourceId: session.id,
                        sourceName: session.activityName || "Aula",
                        user: { uid: currentUser.uid, name: currentUser.displayName || "Sistema" }
                    });
                }
            } else if (isAbsent && !wasAbsent) {
                updates["attendanceStats.absentCount"] = increment(1);
                updates["attendanceStats.lastAbsence"] = sessionDate;
                updates["attendanceStats.consecutiveAbsences"] = increment(1);

                if (wasPresent) {
                    updates["attendanceStats.presentCount"] = increment(-1);
                    totalNewPresents -= 1;
                }
                totalNewAbsents += 1;
            }

            if (Object.keys(updates).length > 0) {
                batch.update(memberRef, updates);
            }

            // Log individual (só quando status mudou)
            const statusChanged = (isPresent && !wasPresent) || (isAbsent && !wasAbsent);
            if (statusChanged) {
                const historyRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS, member.id, COLLECTIONS.ATTENDANCE_LOGS));
                batch.set(historyRef, {
                    idSession,
                    idClass: session.idClass,
                    employeeName: session.employeeName,
                    activityName: session.activityName,
                    date: sessionDate,
                    time: session.startTime,
                    status: isPresent ? "ATTENDED" : "NO_SHOW",
                    justification: member.justification || "",
                    createdAt: serverTimestamp(),
                    performedBy: currentUser.uid,
                });
            }

            // Sync presença da experimental para o enrollment (campo separado)
            if (member.idEnrollment && (member.enrollmentType === "TRIAL" || member.enrollmentType === "trial")) {
                const enrollmentRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS), member.idEnrollment);
                let attendanceStatus = TRIAL_ATTENDANCE_STATUS.PENDING;
                if (isPresent) attendanceStatus = TRIAL_ATTENDANCE_STATUS.PRESENT;
                if (isAbsent) attendanceStatus = TRIAL_ATTENDANCE_STATUS.ABSENT;
                
                batch.update(enrollmentRef, { 
                    attendanceStatus,
                    updatedAt: serverTimestamp() 
                });
            }
        }

        // ─── 4. Analytics Globais (Mês) ───
        if (totalNewPresents !== 0 || totalNewAbsents !== 0) {
            const yearMonth = sessionDate.substring(0, 7).replace("-", "_"); // "2026-03" → "2026_03"
            const analyticsRef = doc(db, "tenants", idTenant, "analytics", `attendance_${yearMonth}`);

            batch.set(analyticsRef, {
                totalPresents: increment(totalNewPresents),
                totalAbsents: increment(totalNewAbsents),
                lastUpdatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // ─── Commit do Batch ───
        await batch.commit();

        // ─── Auditoria ───
        await AuditService.log({
            idTenant,
            user: currentUser,
            module: AUDIT_MODULES.SESSIONS,
            action: AUDIT_ACTIONS.UPDATE,
            entityId: idSession,
            entityName: session.activityName,
            description: `Chamada realizada. Presentes: ${totalNewPresents}, Faltas: ${totalNewAbsents}`,
            details: { presentsRecorded: totalNewPresents, absentsRecorded: totalNewAbsents },
        });

        // ─── Disparo do Motor de Gamificação ───
        if (membersNewlyPresent.length > 0 && gamificationSettings) {
            try {
                await Promise.allSettled(
                    membersNewlyPresent.map(idMember =>
                        GamificationEngineService.evaluateMemberGamification(idTenant, idMember, gamificationSettings, currentUser)
                    )
                );
            } catch (err) {
                console.error("[Gamification] Erro ao disparar engine de gamificação:", err);
            }
        }

        return true;
    }
};

export default AttendanceService;
